
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { KnowledgeSource, KnowledgeSourceConfigType } from '@vendasta/embeddings';
import { AiKnowledgeService } from '../services/ai-knowledge.service';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatListModule } from '@angular/material/list';
import { MatDialog, MatDialogConfig, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom, map, switchMap, take } from 'rxjs';
import { DeleteCustomKnowledgeComponent } from '../delete-custom-knowledge/delete-custom-knowledge.component';
import { MatCardModule } from '@angular/material/card';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { KnowledgeListComponent } from '../knowledge-list/knowledge-list.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { SHOW_BUSINESS_PROFILE_SOURCE_TOKEN } from '../tokens';
import { AiKnowledgeI18nModule } from '../assets/i18n/ai-knowledge-i18n.module';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { STEP_OPTION } from '../knowledge-editor/website-scrape/scraped-pages/scraped-pages.component';
import { AddKnowledgeComponent } from '../knowledge-editor/add-knowledge/add-knowledge.component';
import { StepChangeEvent } from '../knowledge-editor/common/interfaces';
import { WebsiteScrapeComponent } from '../knowledge-editor/website-scrape/website-scrape.component';
import { CustomDataComponent } from '../knowledge-editor/custom-data/custom-data.component';
import { BusinessProfileComponent } from '../knowledge-editor/business-profile/business-profile.component';
import { FileUploadComponent } from '../knowledge-editor/file-upload/file-upload.component';

@Component({
  selector: 'ai-knowledge-knowledge-source-management',
  imports: [
    TranslateModule,
    AiKnowledgeI18nModule,
    GalaxyPageModule,
    MatButtonModule,
    MatIconModule,
    RouterModule,
    MatListModule,
    MatCardModule,
    MatDialogModule,
    KnowledgeListComponent
],
  providers: [AiKnowledgeService],
  templateUrl: './knowledge-source-management.component.html',
  styleUrls: ['./knowledge-source-management.component.scss'],
})
export class KnowledgeSourceManagementComponent implements OnInit {
  @Input() previousPageTitle?: string;
  @Input() previousPageUrl?: string;
  DIALOG_WIDTH = '660px';
  SCRAPED_PAGES_STEP_OPTION = STEP_OPTION;
  private readonly dialog = inject(MatDialog);
  private readonly bpObserver = inject(BreakpointObserver);
  private readonly activeRoute = inject(ActivatedRoute);

  readonly knowledgeSources = signal<KnowledgeSource[]>([]);
  private readonly showBusinessProfileSource = toSignal(inject(SHOW_BUSINESS_PROFILE_SOURCE_TOKEN));
  private knowledgeService = inject(AiKnowledgeService);
  confirmationModal = inject(OpenConfirmationModalService);
  router = inject(Router);

  readonly isLoading = signal(true);

  async ngOnInit(): Promise<void> {
    await this.refreshSources();
    const editSourceParam = this.activeRoute.snapshot.queryParamMap.get('editSource');
    if (editSourceParam) {
      const [sourceId, option] = this.sourceIdAndOptionFromEditSourceParam(editSourceParam);
      const source = this.knowledgeSources().find((s) => s.id == sourceId);
      if (source) {
        await this.editKnowledgeSource(source, option);
      }
    }
    if (this.activeRoute.snapshot.queryParamMap.get('addSource')) {
      await this.addNewKnowledgeSource();
    }
  }

  sourceIdAndOptionFromEditSourceParam(editSourceParam: string): [string, string] {
    const colonIndex = editSourceParam.indexOf(':');
    if (colonIndex === -1) {
      return [editSourceParam, ''];
    } else {
      return [editSourceParam.substring(0, colonIndex), editSourceParam.substring(colonIndex + 1)];
    }
  }

  async refreshSources(): Promise<void> {
    this.isLoading.set(true);
    const knowledgeSources = await this.knowledgeService.listAllKnowledgeSources([
      KnowledgeSourceConfigType.KNOWLEDGE_SOURCE_CONFIG_TYPE_CUSTOM_DATA,
      KnowledgeSourceConfigType.KNOWLEDGE_SOURCE_CONFIG_TYPE_WEBSITE_SCRAPE,
      KnowledgeSourceConfigType.KNOWLEDGE_SOURCE_CONFIG_TYPE_FILE,
    ]);
    if (this.showBusinessProfileSource()) {
      // manually insert business profile source because it may not exist yet
      const businessProfileSource = this.knowledgeService.buildBusinessProfileKnowledgeSource();
      if (businessProfileSource) {
        knowledgeSources.unshift(businessProfileSource);
      }
    }
    this.knowledgeSources.set(knowledgeSources);
    this.isLoading.set(false);
  }

  async addNewKnowledgeSource(): Promise<void> {
    const conf: MatDialogConfig = {
      width: this.DIALOG_WIDTH,
    };

    const isSmallScreen = this.bpObserver.isMatched(Breakpoints.XSmall);
    if (isSmallScreen) {
      conf.maxWidth = '100vw';
      conf.maxHeight = '100vh';
    }
    const editor = this.dialog.open(AddKnowledgeComponent, conf);
    editor.componentInstance.componentSelected
      .pipe(takeUntilDestroyed(editor.componentInstance.destroyRef), take(1))
      .subscribe((event) => {
        this.subscribeToKnowledgeCreated(event.knowledgeCreated, event.destroyRef);
      });
    await firstValueFrom(editor.afterClosed());
  }

  private subscribeToKnowledgeCreated(event: EventEmitter<string>, destroyRef: DestroyRef): void {
    event
      .pipe(
        takeUntilDestroyed(destroyRef),
        take(1),
        switchMap(() => this.refreshSources()),
      )
      .subscribe();
  }
  async editKnowledgeSource(source: KnowledgeSource, option?: string): Promise<void> {
    const conf: MatDialogConfig = {
      width: this.DIALOG_WIDTH,
      data: {
        knowledgeSourceKey: { knowledgeSourceId: source?.id, namespace: source?.namespace },
        config: source?.config,
        stepOption: option,
      },
    };

    const isSmallScreen = this.bpObserver.isMatched(Breakpoints.XSmall);
    if (isSmallScreen) {
      conf.maxWidth = '100vw';
      conf.maxHeight = '100vh';
    }

    let editor: MatDialogRef<StepChangeEvent>;
    if (source.config?.websiteScrapeConfig) {
      editor = this.dialog.open(WebsiteScrapeComponent, conf);
    } else if (source.config?.customDataConfig) {
      editor = this.dialog.open(CustomDataComponent, conf);
    } else if (source.config?.fileConfig) {
      editor = this.dialog.open(FileUploadComponent, conf);
    } else {
      editor = this.dialog.open(BusinessProfileComponent, conf);
    }
    this.setEditSourceQueryParam(source?.id, option);
    editor.componentInstance.stepChange
      .pipe(
        takeUntilDestroyed(editor.componentInstance.destroyRef),
        map((stepOption) => this.setEditSourceQueryParam(source?.id, stepOption)),
      )
      .subscribe();

    const navigatingAway = await firstValueFrom(editor.afterClosed());
    if (!navigatingAway) {
      this.setEditSourceQueryParam(null);
      await this.refreshSources();
    }
  }

  async deleteKnowledgeSource(source: KnowledgeSource): Promise<void> {
    const result = await firstValueFrom(
      this.dialog
        .open(DeleteCustomKnowledgeComponent, {
          width: this.DIALOG_WIDTH,
          data: { knowledgeSourceId: source.id },
        })
        .afterClosed(),
    );
    if (result) {
      await this.refreshSources();
    }
  }

  setEditSourceQueryParam(editSource: string | null, option?: string): void {
    if (option) {
      editSource = editSource + ':' + option;
    }
    const queryParams = { editSource: editSource };
    this.router.navigate([], {
      relativeTo: this.activeRoute,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }
}
